import { UserMinus } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';

type RemoveShiftCaregiverButtonProps = {
    readonly caregiverName: string;
    readonly ehrName?: string;
    readonly onRemove: () => Promise<void> | void;
};

export function RemoveShiftCaregiverButton({
    caregiverName,
    ehrName,
    onRemove,
}: RemoveShiftCaregiverButtonProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    const removeCaregiver = async () => {
        setIsRemoving(true);
        try {
            await onRemove();
            setIsOpen(false);
        } finally {
            setIsRemoving(false);
        }
    };

    return (
        <React.Fragment>
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setIsOpen(true)}
            >
                <UserMinus className="h-4 w-4" />
                Remove caregiver
            </Button>
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    if (!isRemoving) {
                        setIsOpen(open);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Remove the caregiver from this shift?
                        </DialogTitle>
                        <DialogDescription>
                            Phoebe will remove {caregiverName} and mark the
                            shift as needing coverage.
                            {ehrName
                                ? ` Phoebe will also write the removal back to ${ehrName}.`
                                : null}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={isRemoving}
                            onClick={() => setIsOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={isRemoving}
                            onClick={() => void removeCaregiver()}
                        >
                            {isRemoving ? 'Removing…' : 'Remove caregiver'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}
